import React, { useState, useEffect } from 'react';
import { getDashboardStats, getDashboardCharts, getRecentActivities, seedDatabase } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Users, Target, Handshake, FolderKanban, Receipt, TrendingUp,
  TrendingDown, DollarSign, Package, RefreshCw, Loader2, Activity,
  Building2, Globe, ArrowRight, ChevronRight
} from 'lucide-react';

const COLORS = ['#0f766e', '#14b8a6', '#f59e0b', '#3b82f6', '#ef4444'];

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'teal' }) => (
  <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          {trendValue && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${
              trend === 'up' ? 'text-emerald-600' : 'text-red-500'
            }`}>
              {trend === 'up' ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${
          color === 'teal' ? 'bg-teal-50' :
          color === 'blue' ? 'bg-blue-50' :
          color === 'amber' ? 'bg-amber-50' :
          color === 'emerald' ? 'bg-emerald-50' : 'bg-slate-50'
        }`}>
          <Icon className={`h-5 w-5 ${
            color === 'teal' ? 'text-teal-600' :
            color === 'blue' ? 'text-blue-600' :
            color === 'amber' ? 'text-amber-600' :
            color === 'emerald' ? 'text-emerald-600' : 'text-slate-600'
          }`} strokeWidth={1.5} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Master Dashboard Component for Super Admin
const MasterDashboardView = ({ onSelectCompany }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/master-dashboard');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching master dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="master-dashboard-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Master Dashboard</h1>
          <p className="text-slate-500 mt-1">Consolidated view across all companies</p>
        </div>
        <Badge variant="outline" className="text-teal-700 border-teal-300 bg-teal-50">
          <Globe className="h-3 w-3 mr-1" />
          All Companies
        </Badge>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Companies</p>
                <p className="text-2xl font-bold text-slate-900">{data?.total_companies || 0}</p>
              </div>
              <div className="h-10 w-10 bg-teal-100 rounded-full flex items-center justify-center">
                <Building2 className="h-5 w-5 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-2xl font-bold text-slate-900">{data?.total_users || 0}</p>
              </div>
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900">
                  ${(data?.total_revenue || 0).toLocaleString()}
                </p>
              </div>
              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Clients</p>
                <p className="text-2xl font-bold text-slate-900">{data?.total_clients || 0}</p>
              </div>
              <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Cards - Click to select */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Select a Company to View Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.companies?.map((company) => (
            <Card 
              key={company.id} 
              className="cursor-pointer hover:shadow-lg hover:border-teal-300 transition-all group"
              onClick={() => onSelectCompany(company)}
              data-testid={`company-card-${company.code}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">{company.code}</Badge>
                      <span className="font-semibold text-slate-900">{company.name}</span>
                    </div>
                    <p className="text-sm text-slate-500">{company.country}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-teal-600 transition-colors" />
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-slate-500 text-xs">Clients</p>
                    <p className="font-semibold text-slate-900">{company.clients}</p>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-slate-500 text-xs">Invoices</p>
                    <p className="font-semibold text-slate-900">{company.invoices}</p>
                  </div>
                  <div className="bg-green-50 rounded p-2">
                    <p className="text-green-600 text-xs">Revenue</p>
                    <p className="font-semibold text-green-700">${(company.revenue || 0).toLocaleString()}</p>
                  </div>
                  <div className={`rounded p-2 ${(company.revenue - company.expenses) >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <p className={`text-xs ${(company.revenue - company.expenses) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Profit</p>
                    <p className={`font-semibold ${(company.revenue - company.expenses) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      ${((company.revenue || 0) - (company.expenses || 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-teal-600" />
            Company Performance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Company</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Clients</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Projects</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Employees</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Revenue</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Expenses</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Profit</th>
                </tr>
              </thead>
              <tbody>
                {data?.companies?.map((company) => (
                  <tr key={company.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{company.code}</Badge>
                        <span className="font-medium text-slate-900">{company.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right text-slate-900">{company.clients}</td>
                    <td className="py-3 px-4 text-right text-slate-900">{company.projects}</td>
                    <td className="py-3 px-4 text-right text-slate-900">{company.employees}</td>
                    <td className="py-3 px-4 text-right text-green-600 font-medium">
                      ${(company.revenue || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right text-red-600">
                      ${(company.expenses || 0).toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 text-right font-semibold ${
                      (company.revenue - company.expenses) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${((company.revenue || 0) - (company.expenses || 0)).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td className="py-3 px-4 text-slate-900">Total</td>
                  <td className="py-3 px-4 text-right text-slate-900">{data?.total_clients || 0}</td>
                  <td className="py-3 px-4 text-right text-slate-900">{data?.total_projects || 0}</td>
                  <td className="py-3 px-4 text-right text-slate-900">{data?.total_employees || 0}</td>
                  <td className="py-3 px-4 text-right text-green-600">
                    ${(data?.total_revenue || 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-red-600">
                    ${(data?.total_expenses || 0).toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-right ${
                    ((data?.total_revenue || 0) - (data?.total_expenses || 0)) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ${((data?.total_revenue || 0) - (data?.total_expenses || 0)).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Dashboard = () => {
  const { isSuperAdmin, currentCompany, switchCompany } = useAuth();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [showMasterView, setShowMasterView] = useState(true); // Super Admin starts with master view

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, chartsRes, activitiesRes] = await Promise.all([
        getDashboardStats(),
        getDashboardCharts(),
        getRecentActivities()
      ]);
      setStats(statsRes.data);
      setCharts(chartsRes.data);
      setActivities(activitiesRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Non-super admins always see company dashboard
    if (!isSuperAdmin) {
      setShowMasterView(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    // Fetch company-specific data when not in master view
    if (!showMasterView || !isSuperAdmin) {
      fetchData();
    }
  }, [showMasterView, currentCompany]);

  const handleSelectCompany = async (company) => {
    await switchCompany(company);
    setShowMasterView(false);
  };

  const handleBackToMaster = () => {
    setShowMasterView(true);
  };

  const handleSeedDatabase = async () => {
    setSeeding(true);
    try {
      await seedDatabase();
      toast.success('Database seeded with demo data!');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  // Super Admin sees Master Dashboard first
  if (isSuperAdmin && showMasterView) {
    return <MasterDashboardView onSelectCompany={handleSelectCompany} />;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {isSuperAdmin && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleBackToMaster}
              className="mb-2 text-teal-700 hover:text-teal-800 hover:bg-teal-50 -ml-2"
              data-testid="back-to-master-btn"
            >
              <ArrowRight className="h-4 w-4 mr-1 rotate-180" />
              Back to Master Dashboard
            </Button>
          )}
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {currentCompany?.name || 'Dashboard'}
          </h1>
          <p className="text-slate-500 mt-1">
            {currentCompany ? `${currentCompany.country} - ${currentCompany.code}` : 'Welcome to your business overview'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-slate-300"
            data-testid="refresh-dashboard"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleSeedDatabase}
            disabled={seeding}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="seed-database-btn"
          >
            {seeding ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Seeding...
              </>
            ) : (
              'Load Demo Data'
            )}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Clients"
          value={stats?.clients || 0}
          icon={Users}
          color="teal"
        />
        <StatCard
          title="Active Leads"
          value={stats?.leads || 0}
          icon={Target}
          color="blue"
        />
        <StatCard
          title="Active Deals"
          value={stats?.deals || 0}
          icon={Handshake}
          color="amber"
        />
        <StatCard
          title="Active Projects"
          value={stats?.projects || 0}
          icon={FolderKanban}
          color="emerald"
        />
      </div>

      {/* Financial Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats?.total_revenue || 0)}
          icon={DollarSign}
          color="emerald"
        />
        <StatCard
          title="Pending Invoices"
          value={stats?.pending_invoices || 0}
          icon={Receipt}
          color="amber"
        />
        <StatCard
          title="Total Expenses"
          value={formatCurrency(stats?.total_expenses || 0)}
          icon={TrendingDown}
          color="blue"
        />
        <StatCard
          title="Net Profit"
          value={formatCurrency(stats?.net_profit || 0)}
          icon={TrendingUp}
          color="teal"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend */}
        <Card className="lg:col-span-2 bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Revenue vs Expenses</CardTitle>
            <CardDescription>Monthly comparison over the last 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={charts?.revenue_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#0f766e"
                    strokeWidth={2}
                    dot={{ fill: '#0f766e', strokeWidth: 2 }}
                    name="Revenue"
                  />
                  <Line
                    type="monotone"
                    dataKey="expenses"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ fill: '#f59e0b', strokeWidth: 2 }}
                    name="Expenses"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Deals by Stage */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Deals by Stage</CardTitle>
            <CardDescription>Current pipeline distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={charts?.deals_by_stage || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="stage"
                    label={({ stage, count }) => `${count}`}
                    labelLine={false}
                  >
                    {(charts?.deals_by_stage || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expenses by Category */}
        <Card className="lg:col-span-2 bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Expenses by Category</CardTitle>
            <CardDescription>Breakdown of spending</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts?.expenses_by_category || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                  <YAxis type="category" dataKey="category" stroke="#64748b" fontSize={12} width={80} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Bar dataKey="amount" fill="#0f766e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-teal-600" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activities.length > 0 ? (
                activities.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      activity.type === 'client' ? 'bg-teal-500' :
                      activity.type === 'deal' ? 'bg-amber-500' :
                      activity.type === 'invoice' ? 'bg-blue-500' : 'bg-slate-400'
                    }`} />
                    <div>
                      <p className="text-sm text-slate-700">{activity.message}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {activity.time ? new Date(activity.time).toLocaleDateString() : 'Recently'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No recent activity. Click "Load Demo Data" to populate.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Footer */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{stats?.employees || 0}</p>
          <p className="text-sm text-slate-500">Employees</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{stats?.products || 0}</p>
          <p className="text-sm text-slate-500">Products</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{stats?.clients || 0}</p>
          <p className="text-sm text-slate-500">Clients</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{stats?.projects || 0}</p>
          <p className="text-sm text-slate-500">Projects</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
